import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, Clock } from 'lucide-react'
import SEOHead from '../components/SEOHead'
import { scrollToTop } from '../utils/smoothScroll'

const BLOG_JSON_URL = import.meta.env.VITE_BLOG_SERVE_URL || 'https://n8n.planetx.cfd/webhook/blog-posts'

function handlePostClick(e, slug) {
  e.preventDefault()
  scrollToTop(800)
  setTimeout(() => {
    window.location.href = `/blog/${slug}`
  }, 850)
}

function SkeletonCard() {
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden animate-pulse">
      <div className="bg-white/10 aspect-[16/10] rounded-t-2xl" />
      <div className="p-5">
        <div className="h-4 bg-white/10 rounded w-3/4 mb-3" />
        <div className="h-3 bg-white/10 rounded w-full mb-2" />
        <div className="h-3 bg-white/10 rounded w-5/6 mb-4" />
        <div className="flex gap-2 mb-4">
          <div className="h-5 w-16 bg-purple-500/10 rounded-full" />
          <div className="h-5 w-12 bg-purple-500/10 rounded-full" />
        </div>
        <div className="flex gap-3 pt-4 border-t border-white/5">
          <div className="h-3 w-20 bg-white/10 rounded" />
          <div className="h-3 w-16 bg-white/10 rounded" />
        </div>
      </div>
    </div>
  )
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export default function BlogList() {
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    async function fetchPosts() {
      try {
        setLoading(true)
        setError(null)
        const res = await fetch(BLOG_JSON_URL)
        if (!res.ok) throw new Error(`Failed to fetch blog posts (${res.status})`)
        const data = await res.json()
        if (!cancelled) {
          const postsArray = Array.isArray(data) ? data : (data.posts || [])
          const published = postsArray.filter((p) => p.status === 'published')
          published.sort(
            (a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)
          )
          setPosts(published)
        }
      } catch (err) {
        if (!cancelled) setError(err.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchPosts()
    return () => {
      cancelled = true
    }
  }, [])

  const featuredPost = posts.find((p) => p.featured)
  const regularPosts = featuredPost
    ? posts.filter((p) => p.id !== featuredPost.id)
    : posts

  if (loading) {
    return (
      <section className="relative min-h-[100dvh] pt-24 pb-16 px-4 sm:px-6 bg-[#0a0a0f]">
        <SEOHead title="Blog - Nodatx" description="Insights and automation tips from Nodatx" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-purple-600/25 rounded-full blur-[150px] animate-pulse-slow" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[300px] bg-purple-500/10 rounded-full blur-[80px]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
      <div className="relative container-custom max-w-7xl mx-auto px-4 sm:px-6 pt-20">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Our Blog</h1>
            <p className="text-lg text-gray-400">Insights & Automation Tips</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <section className="relative min-h-[100dvh] pt-24 pb-16 px-4 sm:px-6 bg-[#0a0a0f]">
        <SEOHead title="Blog - Nodatx" description="Insights and automation tips from Nodatx" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-purple-600/25 rounded-full blur-[150px] animate-pulse-slow" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[300px] bg-purple-500/10 rounded-full blur-[80px]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
        <div className="relative container-custom max-w-7xl mx-auto px-4 sm:px-6 flex flex-col items-center justify-center min-h-[60vh]">
          <p className="text-red-400 text-lg mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </section>
    )
  }

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": posts.map((post, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "BlogPosting",
        "name": post.title,
        "url": `https://nodatx.com/blog/${post.slug}`,
        "description": post.excerpt,
        "image": post.coverImage,
        "datePublished": post.publishedAt
      }
    }))
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://nodatx.com/" },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://nodatx.com/blog" }
    ]
  }

  return (
    <section className="relative min-h-[100dvh] pt-24 pb-16 px-4 sm:px-6 bg-[#0a0a0f]">
      <SEOHead
        title="Blog — Nodatx Automation Insights"
        description="Explore insights, automation tips, and the latest updates from Nodatx."
        canonical="https://nodatx.com/blog"
        ogImage="https://nodatx.com/og-image.png"
        ogType="website"
        twitterCard="summary_large_image"
        jsonLd={[itemListSchema, breadcrumbSchema]}
      />

      {/* Background effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-purple-600/25 rounded-full blur-[150px] animate-pulse-slow" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[300px] bg-purple-500/10 rounded-full blur-[80px]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />

      <div className="relative container-custom max-w-7xl mx-auto px-4 sm:px-6">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="mb-6">
          <ol className="flex items-center gap-2 text-sm text-gray-500">
            <li><a href="/" className="hover:text-purple-400 transition-colors">Home</a></li>
            <li><span className="text-gray-600">/</span></li>
            <li className="text-gray-300">Blog</li>
          </ol>
        </nav>

        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Our Blog</h1>
          <p className="text-lg text-gray-400">Insights & Automation Tips</p>
        </div>

        {/* Featured Post */}
        {featuredPost && (
          <a
            href={`/blog/${featuredPost.slug}`}
            onClick={(e) => handlePostClick(e, featuredPost.slug)}
            className="block mb-10 relative group cursor-pointer"
          >
            <div className="relative overflow-hidden rounded-2xl border border-white/10 group-hover:border-purple-500/40 transition-all duration-300 group-hover:shadow-[0_0_60px_-15px_rgba(139,92,246,0.4)]">
              <div className="relative aspect-[16/9] sm:aspect-[21/9]">
                <img
                  src={featuredPost.coverImage}
                  alt={featuredPost.title}
                  className="w-full h-full object-cover rounded-2xl"
                  loading="eager"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/50 to-transparent rounded-2xl" />
                <div className="absolute bottom-0 left-0 p-6 sm:p-8">
                  {featuredPost.tags?.slice(0, 2).map((tag) => (
                    <span
                      key={tag}
                      className="inline-block text-xs px-2.5 py-1 bg-purple-500/20 text-purple-300 rounded-full border border-purple-500/30 mr-2 mb-2"
                    >
                      {tag}
                    </span>
                  ))}
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mt-2">
                    {featuredPost.title}
                  </h2>
                </div>
              </div>
              <div className="p-6 sm:p-8">
                <p className="text-gray-400 mb-4 line-clamp-2">{featuredPost.excerpt}</p>
                <div className="flex items-center gap-3 text-gray-500 text-sm">
                  <span className="flex items-center gap-1.5">
                    <Calendar size={14} />
                    {formatDate(featuredPost.publishedAt)}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock size={14} />
                    {featuredPost.readingTime} min read
                  </span>
                </div>
              </div>
            </div>
          </a>
        )}

        {/* Post Grid */}
        {regularPosts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {regularPosts.map((post) => (
              <a
                key={post.id}
                href={`/blog/${post.slug}`}
                onClick={(e) => handlePostClick(e, post.slug)}
                className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 rounded-2xl overflow-hidden hover:border-purple-500/30 hover:shadow-[0_0_40px_-10px_rgba(139,92,246,0.3)] transition-all duration-300 group cursor-pointer"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={post.coverImage}
                    alt={post.title}
                    className="w-full aspect-[16/10] object-cover rounded-t-2xl group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="p-4 sm:p-5">
                  <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2 group-hover:text-purple-400 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-sm text-gray-400 mb-4 line-clamp-3">{post.excerpt}</p>
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="text-xs px-2.5 py-1 bg-purple-500/10 text-purple-300 rounded-full border border-purple-500/20"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-gray-500 text-xs mt-4 pt-4 border-t border-white/5">
                    <span className="flex items-center gap-1.5">
                      <Calendar size={12} />
                      {formatDate(post.publishedAt)}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock size={12} />
                      {post.readingTime} min read
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg">No blog posts available yet.</p>
          </div>
        )}
      </div>
    </section>
  )
}
